import React, { useState, useEffect, useRef } from "react";
import { Home, FileText, X, UserCircle, ChevronDown,PhoneCall } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import Megalogo from "../../utils/images/logo_21.png";
import { decodeJWT } from "../../api/axiosConfig";
import Cookies from 'js-cookie';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const languageDropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = localStorage.getItem('selectedLanguage') || 'az';

    const languages = [
        { code: 'az', label: 'AZ', name: 'Azərbaycanca' },
        { code: 'ru', label: 'RU', name: 'Русский' },
        { code: 'en', label: 'EN', name: 'English' }
    ];


    const translations = {
        az: {
            insuranceCertificates: "Sığorta Şəhadətnamələrim",
            medicalInsuranceCard: "Tibbi Sığorta Kartım",
            contact_us: "Bizimlə Əlaqə"
        },
        ru: {
            insuranceCertificates: "Страховые полисы",
            medicalInsuranceCard: "Страховой мед. полис",
            contact_us: "Связаться с нами",
        },
        en: {
            insuranceCertificates: " Insurance Policies",
            medicalInsuranceCard: " Medical Insurance Card",
            contact_us: "Contact Us"
        }
    };

    const t = translations[currentLanguage as keyof typeof translations];

    const menuItems = [
        {
            icon: Home,
            label: t.insuranceCertificates,
            id: "insurance-certificates",
            path: "/",
        },
        {
            icon: FileText,
            label: t.medicalInsuranceCard,
            id: "medical-insurance-card",
            path: "/health-insurance-card",
        },
        {
            icon: PhoneCall,
            label: t.contact_us,
            id: "contact-us",
            path: "/contact-us",
        },

    ];



    useEffect(() => {
        const accessToken = Cookies.get('accessToken');
        if (accessToken) {
            const decoded = decodeJWT(accessToken);
            if (decoded) {
                setFirstName(decoded.firstName || decoded.first_name || "");
                setLastName(decoded.lastName || decoded.last_name || "");
            }
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
                setIsLanguageDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

  const getActiveIndex = () => {
    const currentPath = location.pathname;

    if (currentPath === '/myprofile') {
      return -1;
    }

    const healthInsurancePaths = [
      '/health-insurance-card',
      '/medicalrequests',
      '/doctor-appointment',
      '/branches'
    ];

    if (healthInsurancePaths.includes(currentPath)) {
      return menuItems.findIndex(item => item.path === '/health-insurance-card');
    }

    const activeIndex = menuItems.findIndex(
      (item) => item.path === currentPath,
    );
    return activeIndex !== -1 ? activeIndex : 0;
  };
    const [activeItem, setActiveItem] = useState(getActiveIndex());

    const handleItemClick = (index: number) => {
        setActiveItem(index);
        navigate(menuItems[index].path);
        onClose();
    };

    const handleProfileClick = () => {
        setActiveItem(-1);
        navigate('/myprofile');
        onClose();
    };

    const handleLanguageChange = (languageCode: string) => {
        localStorage.setItem('selectedLanguage', languageCode);
        window.location.reload();
    };

    React.useEffect(() => {
        if (location.pathname === '/myprofile') {
            setActiveItem(-1);
        } else {
            setActiveItem(getActiveIndex());
        }
    }, [location.pathname]);

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <div
                className={`
                    fixed lg:static left-0 top-0 h-full lg:h-screen w-full bg-colormainbrown text-white z-50
                    transition-transform duration-300 ease-in-out
                    ${isOpen ? "translate-x-0" : "-translate-x-full"}
                    lg:translate-x-0 lg:w-80
                    flex flex-col
                `}
            >
                {/* Logo Section - Fixed height */}
                <div className="p-7 flex items-center justify-between flex-shrink-0 mb-6">
                    <div>
                        <img src={Megalogo} className="w-36" alt="Mega Logo" />
                    </div>
                  <div className="flex items-end bg-white/10 opacity-70 hover:opacity-100 focus:opacity-100 rounded">
                    <button
                      onClick={onClose}
                      className="lg:hidden p-2 rounded-lg transition-colors "
                      aria-label="Bağla"
                    >
                      <X className="w-6 h-6 stroke-white-200" />
                    </button>
                  </div>

                </div>

                {/* Navigation Menu - Scrollable */}
                <nav className="flex-1 px-7 pb-4 overflow-y-auto">
                    <ul className="space-y-2">
                        {menuItems.map((item, index) => (
                            <li key={item.id}>
                                <button
                                    onClick={() => handleItemClick(index)}
                                    className={[
                                        "flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 w-full text-left",
                                        activeItem === index
                                            ? "bg-[rgb(167_167_167_/_0.17)] text-white"
                                            : "text-slate-300 hover:bg-[rgb(167_167_167_/_0.47)] hover:text-white",
                                    ].join(" ")}
                                >
                                    <item.icon className="w-5 h-5 flex-shrink-0" />
                                    <span className="font-medium">{item.label}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* Language Selector Section - Above Profile */}
                <div className="px-7 pb-3 flex-shrink-0">
                    <div className="relative" ref={languageDropdownRef}>
                        <button
                            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                            className="flex items-center justify-between w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                        >
                            <span className="text-sm font-medium">
                                {languages.find(lang => lang.code === currentLanguage)?.name}
                            </span>

                            <ChevronDown
                                className={`w-4 h-4 transition-transform ${!isLanguageDropdownOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {/* Language Dropdown - Opens upward */}
                        {isLanguageDropdownOpen && (
                            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg overflow-hidden z-50">
                                {languages
                                    .filter(lang => lang.code !== currentLanguage)
                                    .map((language) => (
                                        <button
                                            key={language.code}
                                            onClick={() => handleLanguageChange(language.code)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors flex items-center justify-between group"
                                        >
                                            <span className="text-gray-700 text-sm font-medium">
                                                {language.name}
                                            </span>
                                            <span className="text-xs text-gray-500 font-semibold uppercase group-hover:text-gray-700">
                                                {language.label}
                                            </span>
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Profile Section - Fixed at bottom */}
                <div className="p-3 border-t border-white/10 flex-shrink-0">
                    <button
                        onClick={handleProfileClick}
                        className={[
                            "flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 w-full text-left cursor-pointer",
                            location.pathname === '/myprofile'
                                ? "bg-[rgb(167_167_167_/_0.17)] text-white"
                                : "text-slate-300 hover:bg-[rgb(167_167_167_/_0.47)] hover:text-white",
                        ].join(" ")}
                    >
                        <UserCircle className="w-10 h-10 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                                FƏRDİ MƏLUMATLAR
                            </p>
                        </div>
                    </button>
                </div>
            </div>
        </>
    );
};

export default Sidebar;